/**
 * @author       samuel@zumper.com (Samuel Shih)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  ErrorFallback.jsx
 */

/** @jsx jsx */
import { Box, type BoxProps, useColorModeValue, useTheme } from '@chakra-ui/react'
import { css, jsx } from '@emotion/react'
import type { ChakraZ4Type } from '@zcode-ui/chakra-themes'
import { ClearButton, PrimaryButton } from '@zcode-ui/controls'
import { isTruthy } from '@zcode/utils'
import React, { useState } from 'react'
import closeButtonGray from '../../../shared/img/common/close_button_gray.svg'

const closeStyle = css`
  transform: translateY(-4px);
`

export interface ErrorFallbackProps extends BoxProps {
  className?: string
  message: string
  onClickClose?: () => void
  onClickReload?: React.MouseEventHandler<HTMLButtonElement>
}

export const ErrorFallback = ({
  className,
  onClickReload,
  message,
  onClickClose,
  ...rest
}: ErrorFallbackProps) => {
  const [showFallback, setShowFallback] = useState(true)
  const { colors } = useTheme<ChakraZ4Type>()
  const textColor = useColorModeValue(colors.gray['800'], colors.gray['300'])
  if (!showFallback) {
    return null
  }
  return (
    // @ts-expect-error
    <Box<'div'>
      display="flex"
      flexDir="column"
      alignItems="center"
      justifyContent="center"
      className={[className].filter(isTruthy).join(' ')}
      {...rest}
    >
      <Box<'div'>
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb="10px"
        color={textColor}
      >
        {message}
        <ClearButton
          css={closeStyle}
          onClick={() => {
            setShowFallback(false)
            onClickClose?.()
          }}
        >
          <img alt="close" height="18" loading="lazy" src={closeButtonGray} width="18" />
        </ClearButton>
      </Box>
      <PrimaryButton onClick={onClickReload}>Retry</PrimaryButton>
    </Box>
  )
}
